import {type EmailSyncDetails} from '@domain/material-storage'
import {dataTestId} from '@hconnect/uikit'
import {DeleteOutlined} from '@mui/icons-material'
import {Stack, TextField} from '@mui/material'
import React, {FC} from 'react'
import {useForm, Controller} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {DeleteButton, NumberInput} from '../../../common/components'
import {useConfirmDialog} from '../../../common/providers'
import {requiredValidator, minValidator, submitOnBlurAndEnterProps} from '../../../common/utils'
import {useUrlParam} from '../../../routing'
import {
  useAddEmailParserConnection,
  useDeleteDataConnection,
  useEditEmailParserConnection
} from '../../hooks'

interface DataConnectionFormProps {
  storageId: number
  connection?: EmailSyncDetails
  setIsNewConnection: (value: boolean) => void
  isReadOnly: boolean
}

export const EmailParserConnectionForm: FC<DataConnectionFormProps> = ({
  connection,
  storageId,
  setIsNewConnection,
  isReadOnly
}) => {
  const {t} = useTranslation()
  const plantCode = useUrlParam('plantCode')
  const {openDialog} = useConfirmDialog()

  const {mutate: addEmailParserConnection, isPending: isConnectionAdding} =
    useAddEmailParserConnection()
  const {mutate: editEmailParserConnection, isPending: isConnectionEditing} =
    useEditEmailParserConnection()
  const {mutate: deleteDataConnection, isPending: isConnectionDeleting} = useDeleteDataConnection()

  const {
    handleSubmit,
    control,
    reset,
    formState: {isDirty, dirtyFields}
  } = useForm<EmailSyncDetails>({
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: connection ?? {emailColumnName: '', tonneConversionFactor: 1}
  })

  const submit = handleSubmit((values) => {
    if (!isDirty) {
      return
    }
    if (!connection) {
      return addEmailParserConnection(
        {plantCode, storageId, dto: values},
        {
          onSuccess: () => setIsNewConnection(false)
        }
      )
    }
    Object.keys(dirtyFields).forEach((key) => {
      editEmailParserConnection(
        {
          plantCode,
          storageId,
          dto: values,
          key: key as keyof EmailSyncDetails
        },
        {
          onSuccess: ({emailSyncDetails}) =>
            reset({
              emailColumnName: emailSyncDetails?.emailColumnName,
              tonneConversionFactor: emailSyncDetails?.tonneConversionFactor
            })
        }
      )
    })
  })

  const deleteConnection = () => {
    if (!connection) {
      return setIsNewConnection(false)
    }
    openDialog({
      title: t('storagesSettings.deleteEmailParserConnection'),
      description: t('storagesSettings.deleteEmailParserConnectionDescription'),
      mainAction: {
        text: t('common.delete'),
        color: 'error',
        icon: <DeleteOutlined />,
        onAction: () => deleteDataConnection({plantCode, storageId, type: 'email-parser'})
      },
      testId: 'delete_email_parser_connection_dialog'
    })
  }

  const shouldDisableDeleteButton =
    isConnectionAdding || isConnectionDeleting || isConnectionEditing || isReadOnly

  return (
    <Stack
      spacing={2}
      direction="row"
      {...dataTestId('email_parser_connection_form')}
      alignItems="center"
    >
      <Controller
        control={control}
        name="emailColumnName"
        rules={requiredValidator(t)}
        render={({field: {ref, value, onChange}, fieldState: {error}}) => (
          <TextField
            sx={{width: ({spacing}) => spacing(27)}}
            variant="outlined"
            label={t('storagesSettings.emailColumnName')}
            InputLabelProps={{shrink: true}}
            inputRef={ref}
            value={value}
            onChange={onChange}
            helperText={error?.message}
            error={Boolean(error?.message)}
            disabled={isReadOnly}
            {...submitOnBlurAndEnterProps(submit)}
            {...dataTestId('email_column_name_input')}
          />
        )}
      />
      <Controller
        control={control}
        name="tonneConversionFactor"
        rules={{...requiredValidator(t), ...minValidator(t, 0)}}
        render={({field: {ref, value, onChange}, fieldState: {error}}) => (
          <NumberInput
            sx={{width: ({spacing}) => spacing(27)}}
            label={t('storagesSettings.tonneConversionFactor')}
            inputRef={ref}
            value={value}
            onChange={onChange}
            error={error?.message}
            disabled={isReadOnly}
            dataTestId="tonne_conversion_factor_input"
            {...submitOnBlurAndEnterProps(submit)}
          />
        )}
      />
      <div>
        <DeleteButton
          onClick={deleteConnection}
          disabled={shouldDisableDeleteButton}
          {...dataTestId('delete_email_parser_connection_button')}
        />
      </div>
    </Stack>
  )
}
